export type SupportedRecordingFormat = {
  /** Passed into new MediaRecorder(stream, { mimeType }) */
  mimeType: string;
  /** Used for new Blob(chunks, { type }) */
  blobType: string;
  /** File extension used when creating a File */
  extension: 'mp4' | 'webm';
};

/**
 * Pick the best MediaRecorder mimeType supported by the current browser.
 *
 * iOS Safari historically cannot decode WebM (and may record/play MP4 instead).
 * If we record WebM on iOS, the preview often shows black and thumbnail extraction
 * can hang/fail because the browser can't decode the video.
 */
export function pickSupportedRecordingFormat(): SupportedRecordingFormat {
  const candidates: SupportedRecordingFormat[] = [
    // Prefer MP4 first for iOS compatibility.
    { mimeType: 'video/mp4', blobType: 'video/mp4', extension: 'mp4' },
    // Some browsers expose codecs strings for mp4.
    { mimeType: 'video/mp4;codecs=avc1.42E01E,mp4a.40.2', blobType: 'video/mp4', extension: 'mp4' },
    // WebM fallbacks for Chrome/Android.
    { mimeType: 'video/webm;codecs=vp9,opus', blobType: 'video/webm', extension: 'webm' },
    { mimeType: 'video/webm;codecs=vp8,opus', blobType: 'video/webm', extension: 'webm' },
    { mimeType: 'video/webm', blobType: 'video/webm', extension: 'webm' },
  ];

  // If MediaRecorder isn't present (older iOS), caller should handle gracefully.
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return { mimeType: 'video/webm', blobType: 'video/webm', extension: 'webm' };
  }

  const supported = candidates.find((c) => {
    try {
      return MediaRecorder.isTypeSupported(c.mimeType);
    } catch {
      return false;
    }
  });

  return supported ?? { mimeType: 'video/webm', blobType: 'video/webm', extension: 'webm' };
}
